import { AnchorHTMLAttributes, useCallback } from 'react';
import { useRouter } from '../../common/hooks/useRouter';
import { RouterActionEnum, kpHistory } from '../../common/router/history';
import { buildAnalyticClickUrlCode } from '../../utils/browserUtils';
import { DataAnalytics, DataAnalyticsLocations, DataAnalyticsTypes } from './analytics';
import { splunk_message } from '../../common/splunk_constant';
import useSplunkLogger from '../../common/hooks/useSplunkLogger';


const DEFAULT_ANALYTICS_LOC = [DataAnalyticsLocations?.Page, DataAnalyticsLocations?.Body];

export const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

export const buildAnalyticClickExternalUrlCode = (
  url = '',
  clickCode?: string,
  splunkLogger?: any,
) => {
  let code = 'ext';
  try {
    if (clickCode && clickCode !== '') {
      code = clickCode;
    } else {
      if (!url || url === '' || !isAbsoluteUrl(url)) return code;
      const externalUrl = new URL(url);
      code = externalUrl.hostname.replaceAll('.', ':');
      if (externalUrl.pathname && externalUrl.pathname !== '/') {
        code += `-${externalUrl.pathname.replaceAll('/', ':')}`;
      }
    }
  } catch (err) {
    splunkLogger?.({
      logType: 'error',
      data: { ...splunk_message.ERROR.BUILD_ANALYTIC_CLICK_EXTERNAL_URL_CODE_ERROR, error: err, url },
    });
    console.log(err);
  }
  return code;
};

export const buildAnalyticsLocations = (locations: any, splunkLogger?: any) => {
  try {
    if (Array.isArray(locations)) {
      return locations.join(':');
    }
  } catch (err) {
    splunkLogger?.({
      logType: 'error',
      data: { ...splunk_message.ERROR.BUILD_ANALYTICS_LOCATIONS_ERROR, error: err, locations },
    });
    console.log(err);
  }
  return DEFAULT_ANALYTICS_LOC.join(':');
};

interface QuickLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  isExternal?: boolean;
  openLinkInNewTab?: boolean;
  handleLinkClick?: () => void;
  dataAnalytics?: DataAnalytics;
}

export default function QuickLink({
  href,
  isExternal,
  openLinkInNewTab = false,
  handleLinkClick,
  dataAnalytics = {
    type: DataAnalyticsTypes.Link,
    locations: DEFAULT_ANALYTICS_LOC,
  },
  ...props
}: Readonly<QuickLinkProps>): JSX.Element {
  const { pathBuilder } = useRouter();
  const { splunkLogger } = useSplunkLogger();
  const external = isExternal || isAbsoluteUrl(href);

  if (!external && href && href !== '#' && !isAbsoluteUrl(href)) {
    href = pathBuilder(href);
  }

  const handleClick = useCallback(
    (evt: any) => {
      if (href === '#') {
        evt.preventDefault();
        return;
      }

      if (external) {
        handleLinkClick?.();
        return;
      }

      if (handleLinkClick) {
        handleLinkClick();
      } else {
        kpHistory.broadcast({
          location: kpHistory.location,
          action: RouterActionEnum.PUSH,
        });
      }
    },
    [external, href, handleLinkClick],
  );

  const dataAnalyticsLocation = buildAnalyticsLocations(dataAnalytics?.locations, splunkLogger);
  const dataAnalyticsClick = external
    ? buildAnalyticClickExternalUrlCode(href, dataAnalytics?.click, splunkLogger)
    : buildAnalyticClickUrlCode(href, dataAnalytics?.click);

  return (
    <a
      href={href}
      data-testid="externalLink"
      {...props}
      onClick={handleClick}
      data-analytics-type={dataAnalytics?.type}
      data-analytics-click={dataAnalyticsClick}
      data-analytics-location={dataAnalyticsLocation}
      target={(external || openLinkInNewTab ) && isAbsoluteUrl(href) ? '_blank' : '_self'}
      rel="noreferrer"
    />
  );
}
